import { RefObject, useEffect, useLayoutEffect, useRef } from "react";

/** How close to the bottom still counts as "following along", in px. */
const NEAR_BOTTOM_PX = 140;

/**
 * Pins a chronological list to its newest end, the way a message thread
 * behaves.
 *
 * Two rules, and the second matters more than the first:
 *  1. On open, jump to the bottom immediately — no animation, since animating
 *     a scroll the user didn't ask for just looks like the page moved on its
 *     own.
 *  2. On update, scroll only if they were ALREADY near the bottom. Someone who
 *     has scrolled up to check what an earlier round contained is reading;
 *     yanking them back down because a background refresh landed loses their
 *     place, and on this screen that refresh fires every time the tab regains
 *     focus.
 *
 * `signature` should change only when the content actually changes — a refetch
 * returning identical data must not count as an update, or rule 2 fires on a
 * timer for no reason.
 *
 * `scrollerRef` names the element that actually scrolls. It defaults to the
 * document, which is right for a list living in normal page flow (the diner's
 * feed) — but a list inside a fixed, self-scrolling panel (the kitchen ticket)
 * must pass its own container. Getting this wrong fails SILENTLY: rule 2 reads
 * the document's scroll position, finds it pinned at zero, concludes the user
 * has scrolled away, and simply stops following. No error, no clue.
 */
export function useStickToBottom(
  anchorRef: RefObject<HTMLElement | null>,
  signature: string,
  scrollerRef?: RefObject<HTMLElement | null>
): void {
  const hasPinnedRef = useRef(false);
  const lastSignatureRef = useRef<string | null>(null);

  const isNearBottom = (): boolean => {
    const el = scrollerRef?.current ?? document.scrollingElement ?? document.documentElement;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
  };

  // Rule 1. Layout effect + rAF: the anchor exists as soon as React commits,
  // but the content above it hasn't been laid out yet, so scrolling in the
  // same frame lands short.
  useLayoutEffect(() => {
    if (hasPinnedRef.current) return;
    hasPinnedRef.current = true;
    lastSignatureRef.current = signature;

    const frame = requestAnimationFrame(() => {
      anchorRef.current?.scrollIntoView({ block: "end", behavior: "auto" });
    });
    return () => cancelAnimationFrame(frame);
  }, [anchorRef, signature]);

  // Rule 2.
  useEffect(() => {
    if (lastSignatureRef.current === signature) return;
    lastSignatureRef.current = signature;
    if (!isNearBottom()) return;

    const frame = requestAnimationFrame(() => {
      anchorRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
    });
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorRef, signature]);
}

export default useStickToBottom;
